import React, { useEffect } from "react";
import YearRangeField from "./shared/YearRangeField";
import NotesField from "./shared/NotesField";
import ImageUploadSection from "./shared/ImageUploadSection";
import AutocompleteInput from "../../components/AutocompleteInput";
import ItemPrivacyToggle from "./shared/ItemPrivacyToggle";
import "./CardFormShared.css";

export default function DecorForm({
  card,
  setCard,
  onSubmit,
  onCancel,
  imageFiles,
  setImageFiles,
  previews,
  setPreviews,
  existingImages,
  categories = [],
  subcategories = [],
  isEditing = false,
  isSubmitting = false,
}) {
  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setCard((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const filteredSubcategories = subcategories.filter(
    (sub) => String(sub.category_id) === String(card.category_id)
  );

  const selectedSubcategory = subcategories.find(
    (sub) => String(sub.id) === String(card.subcategory_id)
  );

  const isChristmas = selectedSubcategory?.name
    ?.toLowerCase()
    .includes("christmas");

  // Auto-select subcategory when only one exists
  useEffect(() => {
    if (isEditing && card.category_id && !card.subcategory_id) {
      const filtered = subcategories.filter(
        (sub) => String(sub.category_id) === String(card.category_id)
      );
      if (filtered.length === 1) {
        setCard((prev) => ({ ...prev, subcategory_id: filtered[0].id }));
      }
    }
  }, [isEditing, card.category_id, subcategories, setCard]);

  return (
    <form
      onSubmit={onSubmit}
      className={`card-form hobbli-collector-form ${isChristmas ? "christmas-form" : ""}`}
      style={{
        borderRadius: "12px",
        position: "relative",
        background: isChristmas
          ? "linear-gradient(135deg, var(--christmas-bg1), var(--christmas-bg2))"
          : "var(--form-bg)",
        boxShadow: isChristmas
          ? "0 0 25px rgba(255, 0, 0, 0.4)"
          : "0 0 12px rgba(0,0,0,0.15)",
        color: "var(--form-text)",
        transition: "all 0.5s ease",
      }}
    >
      {isChristmas && (
        <>
          <div className="snowflakes">
            {Array.from({ length: 12 }).map((_, i) => (
              <div key={i} className="snowflake">
                {i % 5 === 0 ? "🎅" : "❄️"}
              </div>
            ))}
          </div>
          <div className="christmas-lights top"></div>
          <div className="christmas-lights bottom"></div>
        </>
      )}

      {/* ✨ Animations + Theming */}
      <style>
        {`
        :root {
          --form-bg: #ffffff;
          --form-text: #222;
          --christmas-bg1: #ffefba;
          --christmas-bg2: #ffffff;
        }

        body.dark-mode {
          --form-bg: #1e1e1e !important;
          --form-text: #eee !important;
          --christmas-bg1: #2b2b2b !important;
          --christmas-bg2: #3a3a3a !important;
        }


        /* Snowfall + Santa */
        .snowflakes {
          pointer-events: none;
          position: absolute;
          top: 0;
          left: 0;
          width: 100%;
          height: 100%;
          overflow: hidden;
          z-index: 1;
        }

        .snowflake {
          position: absolute;
          top: -10px;
          font-size: 1.4rem;
          opacity: 0.85;
          animation: snowFall 10s linear infinite;
        }

        @keyframes snowFall {
          0% { transform: translateY(0) rotate(0deg); opacity: 1; }
          100% { transform: translateY(100vh) rotate(360deg); opacity: 0; }
        }

        .snowflake:nth-child(odd) {
          animation-duration: 12s;
          animation-delay: 0s;
        }

        .snowflake:nth-child(even) {
          animation-duration: 9s;
          animation-delay: 3s;
        }

        /* Lights */
        .christmas-lights {
          position: absolute;
          left: 0;
          right: 0;
          height: 10px;
          display: flex;
          justify-content: space-evenly;
          z-index: 2;
        }

        .christmas-lights.top { top: 0; }
        .christmas-lights.bottom { bottom: 0; }

        .christmas-lights::before {
          content: '';
          position: absolute;
          top: 4px;
          left: 0;
          right: 0;
          height: 2px;
          background: var(--form-text);
          opacity: 0.5;
        }

        .christmas-lights::after {
          content: '🔴🟢🟡🔵🟢🔴🟡🔵🔴🟢🟡🔵';
          font-size: 14px;
          animation: twinkle 1.8s infinite alternate;
          text-align: center;
          display: block;
          filter: drop-shadow(0 0 4px rgba(255,255,255,0.8));
        }

        @keyframes twinkle {
          0% { opacity: 0.8; transform: scale(1); }
          50% { opacity: 0.3; transform: scale(1.05); }
          100% { opacity: 1; transform: scale(1); }
        }
      `}
      </style>

      {/* Category Display */}
      {card.category_id && (
        <div className="category-display">
          <strong>
            Category:{" "}
            {categories.find(
              (c) => String(c.id) === String(card.category_id)
            )?.name || "Unknown"}
          </strong>
        </div>
      )}

      {/* Subcategory Dropdown */}
      <label>Subcategory</label>
      <select
        name="subcategory_id"
        value={card.subcategory_id || ""}
        onChange={handleChange}
        disabled={!card.category_id || filteredSubcategories.length === 0}
      >
        <option value="">Select Subcategory</option>
        {filteredSubcategories.map((sub) => (
          <option key={sub.id} value={sub.id}>
            {sub.name}
          </option>
        ))}
      </select>

      {/* Type Dropdown */}
      <label htmlFor="set_name" className="block text-sm font-medium">
        Type
      </label>
      <select
        id="set_name"
        name="set_name"
        value={card.set_name || ""}
        onChange={handleChange}
        className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-green-600 focus:ring-green-600 sm:text-sm"
      >
        <option value="">Select Type</option>
        <option value="Tree">Tree</option>
        <option value="Ornament">Ornament</option>
        <option value="Garland">Garland</option>
        <option value="Lights">Lights</option>
        <option value="Wreath">Wreath</option>
        <option value="Figurine">Figurine</option>
        <option value="Village">Christmas Village</option>
        <option value="Sign">Sign</option>
        <option value="Setup">Setup</option>
        <option value="Other">Other</option>
      </select>

      {/* Name & Brand */}
      <AutocompleteInput
        name="suggested_name"
        placeholder="Name/Descriptor"
        value={card.suggested_name}
        onChange={handleChange}
        rpcName="fuzzy_name_autocomplete"
      />
      <AutocompleteInput
        name="brand"
        placeholder="Brand"
        value={card.brand}
        onChange={handleChange}
        rpcName="fuzzy_brand_autocomplete"
      />

      {/* Number / Year */}
      <input
        name="card_number"
        placeholder="Box Number/Descriptor - Where stored?"
        value={card.card_number}
        onChange={handleChange}
      />
      <YearRangeField card={card} setCard={setCard} />

      {/* Checkboxes */}
        <div style={{ display: "flex", flexDirection: "column", gap: "6px" }}>
          <label
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: "8px",
              cursor: "pointer",
              userSelect: "none",
            }}
          >
            {/* Hidden native checkbox */}
            <input
              type="checkbox"
              name="autographed"
              checked={card.autographed}
              onChange={handleChange}
              style={{ display: "none" }}
            />

            {/* Custom checkbox */}
            <span
              style={{
                width: "20px",
                height: "20px",
                border: "2px solid var(--checkbox-border, #666)",
                borderRadius: "4px",
                display: "inline-block",
                position: "relative",
                backgroundColor: card.autographed
                  ? "var(--checkbox-check, #28a745)"
                  : "var(--checkbox-bg, #fff)",
                transition: "all 0.2s ease",
              }}
            >
              {card.autographed && (
                <span
                  style={{
                    position: "absolute",
                    top: "-2px",
                    left: "2px",
                    fontSize: "16px",
                    color: "#fff",
                    transition: "opacity 0.2s ease",
                  }}
                >
                  ✔
                </span>
              )}
            </span>

            <span>Is Vintage?</span>

            {/* CSS variables for light/dark mode */}
            <style>
              {`
                :root {
                  --checkbox-bg: #fff;
                  --checkbox-border: #666;
                  --checkbox-check: #28a745;
                }

                body.dark-mode {
                  --checkbox-bg: #222 !important;
                  --checkbox-border: #aaa !important;
                  --checkbox-check: #28a745 !important;
                }
              `}
            </style>
          </label>
        </div>

      {/* Notes */}
      <div className="hobbli-notes-section">
      <NotesField value={card.notes} onChange={handleChange} />
      </div>

      {/* Image Upload */}
      <div className="hobbli-images-section">
      <ImageUploadSection
        imageFiles={imageFiles}
        setImageFiles={setImageFiles}
        previews={previews}
        setPreviews={setPreviews}
        existingImages={existingImages}
      />
      </div>

      {/* Buttons */}
      <ItemPrivacyToggle card={card} setCard={setCard} />

      <div className="form-actions">
        <button
          type="submit"
          disabled={isSubmitting}
          style={{
            flex: 1,
            padding: "10px",
            backgroundColor: isSubmitting ? "#999" : "#28a745",
            color: "#fff",
            border: "none",
            borderRadius: "5px",
            cursor: isSubmitting ? "not-allowed" : "pointer",
            opacity: isSubmitting ? 0.7 : 1,
            transition: "background-color 0.2s ease, opacity 0.2s ease",
          }}
        >
          {isSubmitting
            ? (isEditing ? "Saving…" : "Inserting…")
            : (isEditing ? "Save Changes" : "Add Item")}
        </button>
        <button
          type="button"
          onClick={onCancel}
          style={{
            flex: 1,
            padding: "10px",
            backgroundColor: "#6c757d",
            color: "#fff",
            border: "none",
            borderRadius: "5px",
          }}
        >
          Cancel
        </button>
      </div>
    </form>
  );
}
